import type { Dispatch } from 'redux';
import type { CoreUserDDB } from '../../interfaces/user.interface';
import { getAvatarUrl } from '../../services/getPresignedUrlREST';
import {
  SET_USER_AVATAR_KEY,
  SET_USER_AVATAR_URL,
} from '../../store/userAvatar.store';

/**
 * Minimal store interface the helper needs: dispatch + a way to read the
 * current `userAvatarStore.userAvatarKey` at commit time. Defining it
 * structurally keeps the helper decoupled from the Redux store's full type
 * (which isn't exported) while staying readable in tests.
 */
export interface AvatarSyncStore {
  dispatch: Dispatch;
  getState: () => {
    userAvatarStore: { userAvatarKey: string | null };
  };
}

/**
 * TRIL-141: writes both `userAvatarKey` and `userAvatarUrl` to current-user
 * state for every login payload. Branches:
 *
 *  - server presigned URL present → use it directly
 *  - avatar key only             → presign via `getAvatarUrl`; on failure
 *                                  dispatch null (no `buildAvatarUrl`
 *                                  deterministic-URL fallback — that path
 *                                  could collide across users with the same
 *                                  username after delete-account + re-signup)
 *  - neither                     → dispatch null for both. This explicit
 *                                  clear closes the no-avatar carryover
 *                                  gap: without it, a stale URL left in
 *                                  `userAvatarStore` from a prior session
 *                                  race could survive a fresh login that
 *                                  had no avatar.
 *
 * Stale-write guard: the post-await commits in the key branch compare the
 * captured key against the current `userAvatarKey` in the store at commit
 * time. If they don't match — because logout reset the slice or a new
 * login set a different key — we skip the dispatch. A passive useEffect
 * cleanup / AbortSignal isn't sufficient because a pending promise can
 * resolve in the window between a Redux state change and React's commit
 * (when cleanup runs). The store is the source of truth at commit time.
 *
 * Extracted from `UserLogin.tsx` so the test suite exercises the real
 * production code (not a harness duplicate).
 */
export async function syncLoginPayloadAvatar(
  store: AvatarSyncStore,
  loginPayload: CoreUserDDB,
): Promise<void> {
  const { dispatch, getState } = store;
  // TRIL-156: avatar key is canonical at root user.avatar. No nested fallback.
  const key = loginPayload.avatar;
  const serverAvatarUrl = loginPayload.userPreferences?.avatarUrl;
  dispatch(SET_USER_AVATAR_KEY(key ?? null));
  if (serverAvatarUrl) {
    dispatch(SET_USER_AVATAR_URL(serverAvatarUrl));
    return;
  }
  if (key) {
    try {
      const url = await getAvatarUrl(key);
      if (getState().userAvatarStore.userAvatarKey !== key) return;
      dispatch(SET_USER_AVATAR_URL(url));
    } catch {
      if (getState().userAvatarStore.userAvatarKey !== key) return;
      dispatch(SET_USER_AVATAR_URL(null));
    }
    return;
  }
  dispatch(SET_USER_AVATAR_URL(null));
}
